import { c as _c } from "react/compiler-runtime";
import type { SkillFileNode } from '@onefootprint/request-types/dashboard';
import { MarkdownText } from '@onefootprint/ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Components } from 'react-markdown';
import { Paper, PaperFooter, PaperLetterhead } from '@/components/pdf-export';
import type { Bundle, DecisionNode, DecisionQuestion, FieldType, Property, RulesNode, Task } from '../../store/types';
import { isDecisionNode, isManagedNode, isTaskNode } from '../../store/types';
const proseComponents: Components = {
  h1: ({
    children
  }) => <h2 className="text-label-1 text-neutral-900">{children}</h2>,
  h2: ({
    children
  }) => <h3 className="text-label-2 text-neutral-900">{children}</h3>,
  h3: ({
    children
  }) => <h4 className="text-label-3 text-neutral-900">{children}</h4>,
  blockquote: ({
    children
  }) => <blockquote className="pl-4 border-l-2 border-neutral-300 text-neutral-600">{children}</blockquote>,
  hr: () => <hr className="w-full border-0 border-b border-neutral-300 my-3" />,
  a: ({
    children,
    href
  }) => <a href={href} className="underline text-neutral-900">
      {children}
    </a>,
  code: ({
    children
  }) => <code className="font-mono text-[12px] bg-neutral-100 rounded px-1 py-px text-neutral-800">{children}</code>,
  table: ({
    children
  }) => <table className="min-w-full my-3 border border-neutral-300">{children}</table>,
  thead: ({
    children
  }) => <thead className="bg-neutral-50">{children}</thead>,
  th: ({
    children
  }) => <th className="text-caption-3 text-neutral-500 uppercase text-left px-3 py-2 border-b border-neutral-300">
      {children}
    </th>,
  td: ({
    children
  }) => <td className="text-body-3 text-neutral-700 px-3 py-2">{children}</td>,
  tr: ({
    children
  }) => <tr className="border-b border-neutral-200 last:border-b-0">{children}</tr>
};
const Prose = t0 => {
  const $ = _c(2);
  const {
    value
  } = t0;
  let t1;
  if ($[0] !== value) {
    t1 = <div className="text-body-3 leading-relaxed text-neutral-700"><MarkdownText components={proseComponents}>{value}</MarkdownText></div>;
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const Code = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <code className="font-mono text-[12px] bg-neutral-100 border border-neutral-200 rounded px-1 py-px text-neutral-800">{children}</code>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const FieldTypeLabel = t0 => {
  const $ = _c(39);
  const {
    type,
    enumValues
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.exportDoc.procedure.fieldTypes"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  if (type === "number") {
    let t2;
    if ($[1] !== t) {
      t2 = t(_temp);
      $[1] = t;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] !== t2) {
      t3 = <>{t2}</>;
      $[3] = t2;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  if (type === "integer") {
    let t2;
    if ($[5] !== t) {
      t2 = t(_temp2);
      $[5] = t;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    let t3;
    if ($[7] !== t2) {
      t3 = <>{t2}</>;
      $[7] = t2;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    return t3;
  }
  if (type === "boolean") {
    let t2;
    if ($[9] !== t) {
      t2 = t(_temp3);
      $[9] = t;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    let t3;
    if ($[11] !== t2) {
      t3 = <>{t2}</>;
      $[11] = t2;
      $[12] = t3;
    } else {
      t3 = $[12];
    }
    return t3;
  }
  if (type === "enum") {
    let t2;
    if ($[13] !== enumValues || $[14] !== t) {
      t2 = t(_temp4, {
        values: (enumValues ?? []).join(", ")
      });
      $[13] = enumValues;
      $[14] = t;
      $[15] = t2;
    } else {
      t2 = $[15];
    }
    let t3;
    if ($[16] !== t2) {
      t3 = <>{t2}</>;
      $[16] = t2;
      $[17] = t3;
    } else {
      t3 = $[17];
    }
    return t3;
  }
  if (type === "object") {
    let t2;
    if ($[18] !== t) {
      t2 = t(_temp5);
      $[18] = t;
      $[19] = t2;
    } else {
      t2 = $[19];
    }
    let t3;
    if ($[20] !== t2) {
      t3 = <>{t2}</>;
      $[20] = t2;
      $[21] = t3;
    } else {
      t3 = $[21];
    }
    return t3;
  }
  if (type === "array<string>") {
    let t2;
    if ($[22] !== t) {
      t2 = t(_temp6);
      $[22] = t;
      $[23] = t2;
    } else {
      t2 = $[23];
    }
    let t3;
    if ($[24] !== t2) {
      t3 = <>{t2}</>;
      $[24] = t2;
      $[25] = t3;
    } else {
      t3 = $[25];
    }
    return t3;
  }
  if (type === "array<enum>") {
    let t2;
    if ($[26] !== enumValues || $[27] !== t) {
      t2 = t(_temp7, {
        values: (enumValues ?? []).join(", ")
      });
      $[26] = enumValues;
      $[27] = t;
      $[28] = t2;
    } else {
      t2 = $[28];
    }
    let t3;
    if ($[29] !== t2) {
      t3 = <>{t2}</>;
      $[29] = t2;
      $[30] = t3;
    } else {
      t3 = $[30];
    }
    return t3;
  }
  if (type === "array<object>") {
    let t2;
    if ($[31] !== t) {
      t2 = t(_temp8);
      $[31] = t;
      $[32] = t2;
    } else {
      t2 = $[32];
    }
    let t3;
    if ($[33] !== t2) {
      t3 = <>{t2}</>;
      $[33] = t2;
      $[34] = t3;
    } else {
      t3 = $[34];
    }
    return t3;
  }
  let t2;
  if ($[35] !== t) {
    t2 = t(_temp9);
    $[35] = t;
    $[36] = t2;
  } else {
    t2 = $[36];
  }
  let t3;
  if ($[37] !== t2) {
    t3 = <>{t2}</>;
    $[37] = t2;
    $[38] = t3;
  } else {
    t3 = $[38];
  }
  return t3;
};
const PropertyList = t0 => {
  const $ = _c(4);
  const {
    properties
  } = t0;
  let t1;
  if ($[0] !== properties) {
    t1 = properties.map(_temp10);
    $[0] = properties;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <ul className="list-[circle] list-outside pl-5 mt-1 flex flex-col gap-0.5">{t1}</ul>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
const StepHeading = t0 => {
  const $ = _c(12);
  const {
    number,
    name,
    tag
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.exportDoc.procedure"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  let t2;
  if ($[1] !== number || $[2] !== t) {
    t2 = t(_temp11, {
      number
    });
    $[1] = number;
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== name || $[5] !== t2) {
    t3 = <span>{t2} — {name}</span>;
    $[4] = name;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== tag) {
    t4 = tag && <span className="text-[10px] uppercase tracking-wide border border-neutral-400 rounded px-1.5 py-px text-neutral-600">{tag}</span>;
    $[7] = tag;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <h3 className="flex items-baseline gap-2 text-label-2 text-neutral-900">{t3}{t4}</h3>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
};
const TaskStep = t0 => {
  const $ = _c(17);
  const {
    task,
    number
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.exportDoc.procedure"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  let t2;
  if ($[1] !== number || $[2] !== task.name) {
    t2 = <StepHeading number={number} name={task.name} />;
    $[1] = number;
    $[2] = task.name;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== task.prompt) {
    t3 = task.prompt.trim() && <Prose value={task.prompt} />;
    $[4] = task.prompt;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t || $[7] !== task.fields) {
    t4 = task.fields.length > 0 && <div className="flex flex-col gap-1"><p className="text-label-3 text-neutral-900">{t(_temp12)}</p><ul className="list-disc list-outside pl-5 flex flex-col gap-2">{task.fields.map(field => <li key={field.id} className="text-body-3 text-neutral-700"><span className="font-semibold text-neutral-900">{field.name}</span><span className="text-neutral-500">{" "}(<FieldTypeLabel type={field.type} enumValues={field.enumValues} />)</span>{field.prompt.trim() && <div className="mt-0.5"><Prose value={field.prompt} /></div>}{field.children && field.children.length > 0 && <div className="mt-1"><span className="text-neutral-500">{t(_temp13)}</span><PropertyList properties={field.children} /></div>}</li>)}</ul></div>;
    $[6] = t;
    $[7] = task.fields;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t || $[10] !== task.artifacts) {
    t5 = task.artifacts.length > 0 && <div className="flex flex-col gap-1"><p className="text-label-3 text-neutral-900">{t(_temp14)}</p><ul className="list-disc list-outside pl-5 flex flex-col gap-2">{task.artifacts.map(_temp15)}</ul></div>;
    $[9] = t;
    $[10] = task.artifacts;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t2 || $[13] !== t3 || $[14] !== t4 || $[15] !== t5) {
    t6 = <div className="flex flex-col gap-2">{t2}{t3}{t4}{t5}</div>;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
};
const DecisionStep = t0 => {
  const $ = _c(35);
  const {
    node,
    number
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.exportDoc"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  let t2;
  if ($[1] !== t) {
    t2 = question => {
      if (question.kind === "yes_no") {
        return t(_temp16);
      }
      if (question.kind === "pick_one") {
        const values = question.options.map(_temp17).join(", ");
        return t(_temp18, {
          values
        });
      }
      return t(_temp19, {
        max: question.levels.length - 1,
        levels: question.levels.map(_temp20).join("; ")
      });
    };
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const emits = t2;
  let t3;
  if ($[3] !== node.state || $[4] !== t) {
    t3 = node.state.length === 0 ? t(_temp21) : t(_temp22, {
      refs: node.state.map(_temp23).join(", ")
    });
    $[3] = node.state;
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const reads = t3;
  const t4 = node.name;
  let t5;
  if ($[6] !== t) {
    t5 = t(_temp24);
    $[6] = t;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== node.name || $[9] !== number || $[10] !== t5) {
    t6 = <StepHeading number={number} name={t4} tag={t5} />;
    $[8] = node.name;
    $[9] = number;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t) {
    t7 = t(_temp25);
    $[12] = t;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t7) {
    t8 = <p className="text-body-3 text-neutral-700">{t7}</p>;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== node.context) {
    t9 = node.context && <p className="text-body-3 text-neutral-700">{node.context}</p>;
    $[16] = node.context;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== reads) {
    t10 = <p className="text-body-3 text-neutral-700">{reads}</p>;
    $[18] = reads;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== emits || $[21] !== node.questions || $[22] !== t) {
    let t12;
    if ($[24] !== emits || $[25] !== t) {
      t12 = question_0 => <li key={question_0.id} className="text-body-3 text-neutral-700"><Code>{question_0.key}</Code> — {question_0.prompt || t(_temp26)} {emits(question_0)}{question_0.kind === "yes_no" && (question_0.yesMeans || question_0.noMeans) && <ul className="list-[circle] list-outside pl-5">{question_0.yesMeans && <li>{t(_temp27, {
              text: question_0.yesMeans
            })}</li>}{question_0.noMeans && <li>{t(_temp28, {
              text: question_0.noMeans
            })}</li>}</ul>}</li>;
      $[24] = emits;
      $[25] = t;
      $[26] = t12;
    } else {
      t12 = $[26];
    }
    t11 = node.questions.map(t12);
    $[20] = emits;
    $[21] = node.questions;
    $[22] = t;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[27] !== t11) {
    t12 = <ul className="list-disc list-outside pl-5 flex flex-col gap-1">{t11}</ul>;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t10 || $[30] !== t12 || $[31] !== t6 || $[32] !== t8 || $[33] !== t9) {
    t13 = <div className="flex flex-col gap-2">{t6}{t8}{t9}{t10}{t12}</div>;
    $[29] = t10;
    $[30] = t12;
    $[31] = t6;
    $[32] = t8;
    $[33] = t9;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  return t13;
};
const RulesStep = t0 => {
  const $ = _c(22);
  const {
    node,
    number
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.exportDoc"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const t2 = node.name;
  let t3;
  if ($[1] !== t) {
    t3 = t(_temp29);
    $[1] = t;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== node.name || $[4] !== number || $[5] !== t3) {
    t4 = <StepHeading number={number} name={t2} tag={t3} />;
    $[3] = node.name;
    $[4] = number;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t) {
    t5 = t(_temp30);
    $[7] = t;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== node.key) {
    t6 = <Code>{node.key}</Code>;
    $[9] = node.key;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5 || $[12] !== t6) {
    t7 = <p className="text-body-3 text-neutral-700">{t5} {t6}.</p>;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== node.defaultValue || $[15] !== node.groups || $[16] !== t) {
    t8 = node.groups.length === 0 ? <p className="text-body-3 text-neutral-700">{t(_temp31)} <strong className="text-neutral-900">{node.defaultValue}</strong>.</p> : <><ol className="list-decimal list-outside pl-5 flex flex-col gap-2">{node.groups.map(group => <li key={group.id} className="text-body-3 text-neutral-700">{t(_temp32)} <strong className="text-neutral-900">{group.value}</strong>{" "}{t(_temp33)}<ul className="list-disc list-outside pl-5 mt-1 flex flex-col gap-1">{group.conditions.map(_temp34)}</ul></li>)}</ol><p className="text-body-3 text-neutral-700">{t(_temp35)} <strong className="text-neutral-900">{node.defaultValue}</strong>.</p></>;
    $[14] = node.defaultValue;
    $[15] = node.groups;
    $[16] = t;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t4 || $[19] !== t7 || $[20] !== t8) {
    t9 = <div className="flex flex-col gap-2">{t4}{t7}{t8}</div>;
    $[18] = t4;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
};
const SkillTree = t0 => {
  const $ = _c(4);
  const {
    nodes
  } = t0;
  let t1;
  if ($[0] !== nodes) {
    t1 = nodes.map(_temp36);
    $[0] = nodes;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <ul className="list-disc list-outside pl-5 flex flex-col gap-1">{t1}</ul>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
const Section = t0 => {
  const $ = _c(6);
  const {
    number,
    title,
    children
  } = t0;
  let t1;
  if ($[0] !== number || $[1] !== title) {
    t1 = <h2 className="text-label-1 text-neutral-900 pb-1.5 border-b border-neutral-300">{number}. {title}</h2>;
    $[0] = number;
    $[1] = title;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== children || $[4] !== t1) {
    t2 = <section className="flex flex-col gap-3">{t1}{children}</section>;
    $[3] = children;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
type PolicyDocumentProps = {
  bundle: Bundle;
  versionLabel: string;
  generatedOn: string;
  /** Human-readable names of the vendor tools enabled on the agent, resolved from the tools catalog. */
  toolNames: string[];
  /** Tenant name, shown as the letterhead (next to the logo, or alone when there is no logo). */
  tenantName?: string;
  /** Tenant logo as a data URL — pre-rasterized so the PDF capture can embed it (see load-image-as-data-url.ts). */
  tenantLogoSrc?: string;
};
const PolicyDocument = t0 => {
  const $ = _c(108);
  const {
    bundle,
    versionLabel,
    generatedOn,
    toolNames,
    tenantName,
    tenantLogoSrc
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.exportDoc"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  let sections;
  if ($[1] !== bundle.inputs || $[2] !== bundle.nodes || $[3] !== bundle.norms || $[4] !== bundle.skills || $[5] !== t || $[6] !== toolNames) {
    sections = [];
    if (bundle.norms.trim()) {
      let t2;
      if ($[8] !== t) {
        t2 = t(_temp37);
        $[8] = t;
        $[9] = t2;
      } else {
        t2 = $[9];
      }
      let t3;
      if ($[10] !== bundle.norms) {
        t3 = <Prose value={bundle.norms} />;
        $[10] = bundle.norms;
        $[11] = t3;
      } else {
        t3 = $[11];
      }
      let t4;
      if ($[12] !== t2 || $[13] !== t3) {
        t4 = {
          key: "norms",
          title: t2,
          content: t3
        };
        $[12] = t2;
        $[13] = t3;
        $[14] = t4;
      } else {
        t4 = $[14];
      }
      sections.push(t4);
    }
    if (bundle.inputs.length > 0) {
      let t2;
      if ($[15] !== t) {
        t2 = t(_temp38);
        $[15] = t;
        $[16] = t2;
      } else {
        t2 = $[16];
      }
      let t3;
      if ($[17] !== t) {
        t3 = t(_temp39);
        $[17] = t;
        $[18] = t3;
      } else {
        t3 = $[18];
      }
      let t4;
      if ($[19] !== t3) {
        t4 = <span className="text-caption-3 uppercase text-neutral-500">{t3}</span>;
        $[19] = t3;
        $[20] = t4;
      } else {
        t4 = $[20];
      }
      let t5;
      if ($[21] !== t) {
        t5 = t(_temp40);
        $[21] = t;
        $[22] = t5;
      } else {
        t5 = $[22];
      }
      let t6;
      if ($[23] !== t5) {
        t6 = <span className="text-caption-3 uppercase text-neutral-500">{t5}</span>;
        $[23] = t5;
        $[24] = t6;
      } else {
        t6 = $[24];
      }
      let t7;
      if ($[25] !== t) {
        t7 = t(_temp41);
        $[25] = t;
        $[26] = t7;
      } else {
        t7 = $[26];
      }
      let t8;
      if ($[27] !== t7) {
        t8 = <span className="text-caption-3 uppercase text-neutral-500">{t7}</span>;
        $[27] = t7;
        $[28] = t8;
      } else {
        t8 = $[28];
      }
      let t9;
      if ($[29] !== t4 || $[30] !== t6 || $[31] !== t8) {
        t9 = <div className="grid grid-cols-[220px_100px_1fr] gap-x-6 pb-1.5 border-b border-neutral-400">{t4}{t6}{t8}</div>;
        $[29] = t4;
        $[30] = t6;
        $[31] = t8;
        $[32] = t9;
      } else {
        t9 = $[32];
      }
      let t10;
      if ($[33] !== bundle.inputs || $[34] !== t) {
        let t11;
        if ($[36] !== t) {
          t11 = input => <div key={input.id} className="grid grid-cols-[220px_100px_1fr] gap-x-6 py-2 border-b border-neutral-200"><span><Code>{input.name}</Code></span><span className="text-body-3 text-neutral-700">{input.required ? t(_temp42) : t(_temp43)}</span><span className="text-body-3 text-neutral-700">{input.description || "\u2014"}</span></div>;
          $[36] = t;
          $[37] = t11;
        } else {
          t11 = $[37];
        }
        t10 = bundle.inputs.map(t11);
        $[33] = bundle.inputs;
        $[34] = t;
        $[35] = t10;
      } else {
        t10 = $[35];
      }
      let t11;
      if ($[38] !== t10 || $[39] !== t9) {
        t11 = <div className="flex flex-col">{t9}{t10}</div>;
        $[38] = t10;
        $[39] = t9;
        $[40] = t11;
      } else {
        t11 = $[40];
      }
      let t12;
      if ($[41] !== t11 || $[42] !== t2) {
        t12 = {
          key: "inputs",
          title: t2,
          content: t11
        };
        $[41] = t11;
        $[42] = t2;
        $[43] = t12;
      } else {
        t12 = $[43];
      }
      sections.push(t12);
    }
    if (bundle.nodes.length > 0) {
      let t2;
      if ($[44] !== t) {
        t2 = t(_temp44);
        $[44] = t;
        $[45] = t2;
      } else {
        t2 = $[45];
      }
      let t3;
      if ($[46] !== bundle.nodes) {
        t3 = bundle.nodes.map(_temp45);
        $[46] = bundle.nodes;
        $[47] = t3;
      } else {
        t3 = $[47];
      }
      let t4;
      if ($[48] !== t3) {
        t4 = <div className="flex flex-col gap-6">{t3}</div>;
        $[48] = t3;
        $[49] = t4;
      } else {
        t4 = $[49];
      }
      let t5;
      if ($[50] !== t2 || $[51] !== t4) {
        t5 = {
          key: "procedure",
          title: t2,
          content: t4
        };
        $[50] = t2;
        $[51] = t4;
        $[52] = t5;
      } else {
        t5 = $[52];
      }
      sections.push(t5);
    }
    if (toolNames.length > 0) {
      let t2;
      if ($[53] !== t) {
        t2 = t(_temp46);
        $[53] = t;
        $[54] = t2;
      } else {
        t2 = $[54];
      }
      let t3;
      if ($[55] !== toolNames) {
        t3 = toolNames.map(_temp47);
        $[55] = toolNames;
        $[56] = t3;
      } else {
        t3 = $[56];
      }
      let t4;
      if ($[57] !== t3) {
        t4 = <ul className="list-disc list-outside pl-5 flex flex-col gap-1">{t3}</ul>;
        $[57] = t3;
        $[58] = t4;
      } else {
        t4 = $[58];
      }
      let t5;
      if ($[59] !== t2 || $[60] !== t4) {
        t5 = {
          key: "tools",
          title: t2,
          content: t4
        };
        $[59] = t2;
        $[60] = t4;
        $[61] = t5;
      } else {
        t5 = $[61];
      }
      sections.push(t5);
    }
    if (bundle.skills.length > 0) {
      let t2;
      if ($[62] !== t) {
        t2 = t(_temp48);
        $[62] = t;
        $[63] = t2;
      } else {
        t2 = $[63];
      }
      let t3;
      if ($[64] !== t) {
        t3 = t(_temp49);
        $[64] = t;
        $[65] = t3;
      } else {
        t3 = $[65];
      }
      let t4;
      if ($[66] !== t3) {
        t4 = <p className="text-body-3 text-neutral-500 italic">{t3}</p>;
        $[66] = t3;
        $[67] = t4;
      } else {
        t4 = $[67];
      }
      let t5;
      if ($[68] !== bundle.skills) {
        t5 = <SkillTree nodes={bundle.skills} />;
        $[68] = bundle.skills;
        $[69] = t5;
      } else {
        t5 = $[69];
      }
      let t6;
      if ($[70] !== t4 || $[71] !== t5) {
        t6 = <div className="flex flex-col gap-2">{t4}{t5}</div>;
        $[70] = t4;
        $[71] = t5;
        $[72] = t6;
      } else {
        t6 = $[72];
      }
      let t7;
      if ($[73] !== t2 || $[74] !== t6) {
        t7 = {
          key: "skills",
          title: t2,
          content: t6
        };
        $[73] = t2;
        $[74] = t6;
        $[75] = t7;
      } else {
        t7 = $[75];
      }
      sections.push(t7);
    }
    $[1] = bundle.inputs;
    $[2] = bundle.nodes;
    $[3] = bundle.norms;
    $[4] = bundle.skills;
    $[5] = t;
    $[6] = toolNames;
    $[7] = sections;
  } else {
    sections = $[7];
  }
  let t2;
  if ($[76] !== t) {
    t2 = t(_temp50);
    $[76] = t;
    $[77] = t2;
  } else {
    t2 = $[77];
  }
  let t3;
  if ($[78] !== t) {
    t3 = t(_temp51);
    $[78] = t;
    $[79] = t3;
  } else {
    t3 = $[79];
  }
  let t4;
  if ($[80] !== t3 || $[81] !== versionLabel) {
    t4 = {
      label: t3,
      value: versionLabel
    };
    $[80] = t3;
    $[81] = versionLabel;
    $[82] = t4;
  } else {
    t4 = $[82];
  }
  let t5;
  if ($[83] !== t) {
    t5 = t(_temp52);
    $[83] = t;
    $[84] = t5;
  } else {
    t5 = $[84];
  }
  let t6;
  if ($[85] !== generatedOn || $[86] !== t5) {
    t6 = {
      label: t5,
      value: generatedOn
    };
    $[85] = generatedOn;
    $[86] = t5;
    $[87] = t6;
  } else {
    t6 = $[87];
  }
  let t7;
  if ($[88] !== t4 || $[89] !== t6) {
    t7 = [t4, t6];
    $[88] = t4;
    $[89] = t6;
    $[90] = t7;
  } else {
    t7 = $[90];
  }
  let t8;
  if ($[91] !== bundle.name || $[92] !== t2 || $[93] !== t7 || $[94] !== tenantLogoSrc || $[95] !== tenantName) {
    t8 = <PaperLetterhead kicker={t2} meta={t7} tenantLogoSrc={tenantLogoSrc} tenantName={tenantName} title={bundle.name} />;
    $[91] = bundle.name;
    $[92] = t2;
    $[93] = t7;
    $[94] = tenantLogoSrc;
    $[95] = tenantName;
    $[96] = t8;
  } else {
    t8 = $[96];
  }
  let t9;
  if ($[97] !== sections) {
    t9 = sections.map(_temp53);
    $[97] = sections;
    $[98] = t9;
  } else {
    t9 = $[98];
  }
  let t10;
  if ($[99] !== bundle.name || $[100] !== t) {
    t10 = t(_temp54, {
      name: bundle.name
    });
    $[99] = bundle.name;
    $[100] = t;
    $[101] = t10;
  } else {
    t10 = $[101];
  }
  let t11;
  if ($[102] !== t10) {
    t11 = <PaperFooter note={t10} />;
    $[102] = t10;
    $[103] = t11;
  } else {
    t11 = $[103];
  }
  let t12;
  if ($[104] !== t11 || $[105] !== t8 || $[106] !== t9) {
    t12 = <Paper>{t8}{t9}{t11}</Paper>;
    $[104] = t11;
    $[105] = t8;
    $[106] = t9;
    $[107] = t12;
  } else {
    t12 = $[107];
  }
  return t12;
};
export default PolicyDocument;
function _temp($) {
  return $.number;
}
function _temp2($_0) {
  return $_0.integer;
}
function _temp3($_1) {
  return $_1.boolean;
}
function _temp4($_2) {
  return $_2.enum;
}
function _temp5($_3) {
  return $_3.object;
}
function _temp6($_4) {
  return $_4.arrayString;
}
function _temp7($_5) {
  return $_5.arrayEnum;
}
function _temp8($_6) {
  return $_6.arrayObject;
}
function _temp9($_7) {
  return $_7.text;
}
function _temp10(property) {
  return <li key={property.id} className="text-body-3 text-neutral-700"><span className="font-medium text-neutral-900">{property.name}</span><span className="text-neutral-500">{" "}(<FieldTypeLabel type={property.type} enumValues={property.enumValues} />)</span>{property.description ? <span> — {property.description}</span> : null}{property.children && property.children.length > 0 && <PropertyList properties={property.children} />}</li>;
}
function _temp11($) {
  return $.stepLabel;
}
function _temp12($) {
  return $.determinations;
}
function _temp13($_0) {
  return $_0.includes;
}
function _temp14($_1) {
  return $_1.artifacts;
}
function _temp15(artifact) {
  return <li key={artifact.id} className="text-body-3 text-neutral-700"><span className="font-semibold text-neutral-900">{artifact.name}</span><span className="text-neutral-500 uppercase text-[11px]"> · {artifact.type}</span>{artifact.spec.trim() && <div className="mt-0.5"><Prose value={artifact.spec} /></div>}</li>;
}
function _temp16($) {
  return $.decision.yesNo;
}
function _temp17(option) {
  return option.description ? `${option.value} (${option.description})` : option.value;
}
function _temp18($_0) {
  return $_0.decision.pickOne;
}
function _temp19($_1) {
  return $_1.decision.score;
}
function _temp20(level, index) {
  return `${index} = ${level.description}`;
}
function _temp21($_2) {
  return $_2.decision.readsEverything;
}
function _temp22($_3) {
  return $_3.decision.readsOnly;
}
function _temp23(ref) {
  return `${ref.source}s.${ref.key}`;
}
function _temp24($_4) {
  return $_4.procedure.decisionTag;
}
function _temp25($_5) {
  return $_5.decision.intro;
}
function _temp26($_6) {
  return $_6.decision.noPrompt;
}
function _temp27($_7) {
  return $_7.decision.yesMeans;
}
function _temp28($_8) {
  return $_8.decision.noMeans;
}
function _temp29($) {
  return $.procedure.decisionTag;
}
function _temp30($_0) {
  return $_0.rules.intro;
}
function _temp31($_1) {
  return $_1.rules.noGroups;
}
function _temp32($_2) {
  return $_2.rules.setTo;
}
function _temp33($_3) {
  return $_3.rules.groupWhen;
}
function _temp34(condition) {
  return <li key={condition.id}><Code>{condition.expression}</Code></li>;
}
function _temp35($_4) {
  return $_4.rules.otherwise;
}
function _temp36(node) {
  return <li key={node.name} className="text-body-3 text-neutral-700">{node.kind === "folder" ? <><span className="font-semibold text-neutral-900">{node.name}/</span>{node.children.length > 0 && <SkillTree nodes={node.children} />}</> : <span className="font-mono text-[12px]">{node.name}</span>}</li>;
}
function _temp37($) {
  return $.sections.norms;
}
function _temp38($_0) {
  return $_0.sections.inputs;
}
function _temp39($_1) {
  return $_1.inputs.field;
}
function _temp40($_2) {
  return $_2.inputs.presence;
}
function _temp41($_3) {
  return $_3.inputs.description;
}
function _temp42($_4) {
  return $_4.inputs.required;
}
function _temp43($_5) {
  return $_5.inputs.optional;
}
function _temp44($_6) {
  return $_6.sections.procedure;
}
function _temp45(node, index) {
  if (isTaskNode(node)) {
    return <TaskStep key={node.id} task={node} number={index + 1} />;
  }
  if (isManagedNode(node)) {
    return <TaskStep key={node.id} task={node.task} number={index + 1} />;
  }
  if (isDecisionNode(node)) {
    return <DecisionStep key={node.id} node={node} number={index + 1} />;
  }
  return <RulesStep key={node.id} node={node} number={index + 1} />;
}
function _temp46($_7) {
  return $_7.sections.tools;
}
function _temp47(name) {
  return <li key={name} className="text-body-3 text-neutral-700">{name}</li>;
}
function _temp48($_8) {
  return $_8.sections.skills;
}
function _temp49($_9) {
  return $_9.skills.hint;
}
function _temp50($_10) {
  return $_10.subtitle;
}
function _temp51($_11) {
  return $_11.meta.version;
}
function _temp52($_12) {
  return $_12.meta.generated;
}
function _temp53(section, index_0) {
  return <Section key={section.key} number={index_0 + 1} title={section.title}>{section.content}</Section>;
}
function _temp54($_13) {
  return $_13.footer;
}